import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {  faCircleMinus, faEllipsis, faPlus, faArrowDownShortWide, faArrowDownWideShort, faSearch } from "@fortawesome/pro-regular-svg-icons";
import InputField from "../../components/InputField";
import CustomDropdown from "../../components/CustomDropdown";
import Modal from "../../components/Modal";
import Dropdown from "../../components/Dropdown";
import toast from 'react-hot-toast';
import { getAllDiscounts } from "../../api/Discounts";
import { useAuth } from "../../providers/AuthContext";
import { useTranslation } from "react-i18next";

const DiscountTable = ({ addModal, setAddModal, addDiscountState, setAddDiscountState, discounts = [], setConfirmState, setDiscounts, setHasChanges, setAddDiscountModal }) => {
  const { t } = useTranslation();

  const [filteredDiscounts, setFilteredDiscounts] = useState(discounts);
  const [sortColumn, setSortColumn] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [search, setSearch] = useState('');

  useEffect(() => {
    applyFilterAndSort();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [search, discounts, sortColumn, sortOrder]);

  const addDiscount = async (newDiscounts) => {
    setHasChanges(true);
    newDiscounts.forEach(newDiscount => {
      const isExisting = discounts.find((d) => d.name === newDiscount.name && d.minKwh === newDiscount.minKwh && d.maxKwh === newDiscount.maxKwh);

      if (isExisting && !isExisting.isDeleted) {
        toast.error(t("toast.alreadyExists", { entity: t("entity.discount") }));
      } else if (isExisting && isExisting.isDeleted) {
        setDiscounts((d) => d.map((discount) => {
          if (discount.name === newDiscount.name && discount.minKwh === newDiscount.minKwh && discount.maxKwh === newDiscount.maxKwh) {
            return { ...discount, isDeleted: false };
          }
          return discount;
        }));
      } else {
        setDiscounts((d) => ([...d, { ...newDiscount, isNew: true }]));
      }
    });
    setAddDiscountModal(false);
    setAddDiscountState([]);
  }

  const removeDiscount = (name, minKwh, maxKwh) => {
    setHasChanges(true);
    setDiscounts((prev) =>
      prev.map((discount) => {
        // If it's a new discount that hasn't been saved yet (isNew), remove it completely.
        if (discount.name === name && discount.minKwh === minKwh && discount.maxKwh === maxKwh && discount.isNew) {
          return null; // We'll filter this out later
        }

        // Otherwise, mark the discount as isDeleted
        if (discount.name === name && discount.minKwh === minKwh && discount.maxKwh === maxKwh) {
          return { ...discount, isDeleted: true };
        }

        return discount;
      }).filter(Boolean) // Filter out all null (i.e., newly created discounts that were removed before saving)
    );
  };

  const applyFilterAndSort = () => {
    let filtered = discounts.filter((i) => !i.isDeleted);

    // Filter based on search
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.thirdPartySystemName.toLowerCase().includes(searchLower) ||
          item.thirdPartySystemType.toLowerCase().includes(searchLower) ||
          item.thirdPartySystemExternalId.toLowerCase().includes(searchLower)
      );
    }

    // Sort based on selected column
    const sorted = [...filtered].sort((a, b) => {
      let aValue = a[sortColumn]?.toString().toLowerCase();
      let bValue = b[sortColumn]?.toString().toLowerCase();

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredDiscounts(sorted);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return sortOrder === 'asc' ? faArrowDownShortWide : faArrowDownWideShort;
    }
    return null;
  };

  return (
    <div className="flex flex-col w-full">
      <div className="flex flex-row justify-between items-center">
        <h4 className="mb-2">{t("entity.discounts")}</h4>
        <div className="flex justify-end">
          <div className="mb-2">
            <InputField icon={faSearch} placeholder={t("search.discount")} value={search} onChange={(v) => { setSearch(v.target.value) }} />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto pb-[250px] mb-[-250px]">
        <table className="table-default w-full">
          <thead>
            <tr>
              <th className="cursor-pointer" onClick={() => handleSort('name')}>
                {t("common.name")} {getSortIcon('name') && <FontAwesomeIcon icon={getSortIcon('name')} className="w-4 pl-2" />}
              </th>
              <th className="cursor-pointer" onClick={() => handleSort('minKwh')}>
                {t("table.minKwh")} {getSortIcon('minKwh') && <FontAwesomeIcon icon={getSortIcon('minKwh')} className="w-4 pl-2" />}
              </th>
              <th className="cursor-pointer" onClick={() => handleSort('maxKwh')}>
                {t("table.maxKwh")} {getSortIcon('maxKwh') && <FontAwesomeIcon icon={getSortIcon('maxKwh')} className="w-4 pl-2" />}
              </th>
              <th className="cursor-pointer" onClick={() => handleSort('discountValue')}>
                {t("table.discountPerKwh")} {getSortIcon('discountValue') && <FontAwesomeIcon icon={getSortIcon('discountValue')} className="w-4 pl-2" />}
              </th>
              <th>
                <div className="flex w-full justify-end items-center">
                  <button
                    className="block text-sm text-primary-600 hover:text-primary-400 text-left rounded-lg"
                    onClick={() => { setAddModal(true) }}
                  >
                    <FontAwesomeIcon icon={faPlus} /> {t("common.addNew")}
                  </button>
                  <DiscountModal addModal={addModal} setAddModal={setAddModal} discounts={discounts} addDiscountState={addDiscountState} setAddDiscountState={setAddDiscountState} addDiscount={addDiscount} />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredDiscounts.map((discount, index) => (
              
              <tr key={index}>
                <td>{discount.name}</td>
                <td>{discount.minKwh}</td>
                <td>{discount.maxKwh}</td>
                <td>{discount.discountValueType === "Fixed" ? `${discount.discountValue} kr/kWh`: `${discount.discountValue}%`}</td>
                <td className="text-center flex justify-end">
                  <CustomDropdown isRight={true}
                    ContentComponent={() => (
                      <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                        <button
                          className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                          onClick={() => {
                            setConfirmState((s) => ({
                              ...s,
                              isOpen: true,
                              id: discount.name,
                              title: t("confirm.deleteEntity", { entity: t("entity.discount"), name: discount.name }),
                              confirm: () => { removeDiscount(discount.name, discount.minKwh, discount.maxKwh) }
                            }));
                          }}
                        >

                          <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}

                        </button>
                      </div>
                    )}
                  >
                    <div className="w-8 h-6 flex justify-end bg-red items-center cursor-pointer">
                      <FontAwesomeIcon icon={faEllipsis} />
                    </div>
                  </CustomDropdown>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
};

export default DiscountTable;


const DiscountModal = ({ addModal, setAddModal, addDiscount = () => { } }) => {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const [discounts, setDiscounts] = useState([]);
  const [selectedDiscounts, setSelectedDiscounts] = useState([]);

  useEffect(() => {
    const handleGetDiscounts = async () => {
      try {
        const accessToken = await getAccessToken();
        const newDiscounts = await getAllDiscounts(accessToken);
        setDiscounts(newDiscounts);  // Set discounts once fetched

      } catch (error) {
        console.error(error);
      }
    };
    if(discounts.length === 0){
      handleGetDiscounts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, []);

  const handleAddDiscounts = () => {
    var newDiscounts = [];
    selectedDiscounts.forEach((selectedDiscount) => {
      const discount = discounts.find((item) => item.name === selectedDiscount);
      if (discount) {
        newDiscounts.push(discount);
      }
    });
    addDiscount(newDiscounts)
    setSelectedDiscounts([])
  }

  return (
    <Modal isOpen={addModal} setIsOpen={() => setAddModal(false)}>
      <div className="flex flex-col justify-start gap-4 items-start">
        <h3>{t("entity.discount")}</h3>
        <Dropdown
          label={t("entity.discount")}
          searchable={true}
          options={discounts?.map((site) => site.name)} // Show site names in the dropdown
          selectedOptions={selectedDiscounts} // Use chargingSiteName to show in the dropdown
          multiSelect={true}
          onSelect={(selectedName) => {

            setSelectedDiscounts(selectedName);
          }}
        />

        <div className="flex gap-2 w-full justify-end">
          <button className="btn btn-outline-primary" onClick={() => setAddModal(false)}>{t("common.cancel")}</button>

          <button className="btn btn-primary" disabled={selectedDiscounts.length === 0} onClick={() => { handleAddDiscounts() }}>{t("common.add")}</button>
        </div>
      </div>
    </Modal>
  );
};